<!--
  The body of the dispatch board's "Set driver or subhauler" dialog (Mark,
  2026-10-10: "we have just a basic dropdown that causes us to have to select
  the driver/subhauler to even see if they are available. Instead of the
  dropdown, let's make this a datatable select where we have driver name /
  availability and the option to assign ... and if we're overriding the
  availability, then we open the note spot and save").

  One table: every driver with the server's availability decision for THIS
  load already filled in (GET /scheduling/availability?load_id=, the
  all-drivers form, which also carries the travel legs to and from their
  neighbouring loads), then every active subhauler. Each row has a Select
  button that assigns at once. A driver who needs an override has a button
  that opens the reason step instead: the reasons, a note box, and "Assign
  with reason". A driver nothing can talk past (already on another load, or
  no scheduling rights to override) is listed with the reason and no button,
  never hidden.

  This component only decides; it emits `assign` with {kind, id, reason} and
  the board makes the PATCH, so the 409 path, the toast and the refresh stay
  where they were. A refusal comes back in as `problem` and is shown here.

  Spec 46 (plan §4.14, "integrate, don't build"): beside the availability
  read it asks GET /hos/clocks, the drivers' hours-of-service clocks as the
  carrier's own ELD provider reports them, and prints one line per matched
  driver ("6h 10m drive left · Samsara · 2 min ago") with a footer saying
  where they come from and that Ladeway does not check hours of service.
  Display only: the clocks never colour, sort, hide or disable a row, the
  table never waits for them, and a failed read shows nothing about HOS.
-->
<template>
  <div class="lw-p-assignee-picker">
    <template v-if="pending">
      <p class="lw-p-assignee-picker__step-title">
        Assign <strong>{{ pending.name }}</strong> anyway?
      </p>
      <AvailabilityNotice :decision="pending" :zone="zone" :can-override="canOverride" />
      <LwField
        v-model="reason"
        type="textarea"
        :rows="3"
        :maxlength="2000"
        label="Reason to assign anyway"
        description="Recorded on the load with the assignment."
        data-test="assignee-reason"
      />
      <LwNotice v-if="problem" tone="danger" :title="problem.title">
        <ul v-if="problem.reasons && problem.reasons.length" class="lw-p-assignee-picker__list">
          <li v-for="(line, i) in problem.reasons" :key="i">{{ line }}</li>
        </ul>
      </LwNotice>
      <div class="lw-p-assignee-picker__step-actions">
        <LwButton variant="secondary" :disabled="busy" @click="back">Back to the list</LwButton>
        <LwButton
          variant="primary"
          :loading="busy"
          :disabled="!reason.trim()"
          data-test="assignee-assign-with-reason"
          @click="assignPending"
        >
          Assign with reason
        </LwButton>
      </div>
    </template>

    <template v-else>
      <p v-if="hint" class="lw-p-assignee-picker__hint" data-test="assignee-hint">{{ hint }}</p>
      <!--
        Spec 39 §7: with acceptance on, a driver must accept the load, and
        this is how long they have (the tenant default, changeable per
        assignment; 0 means no deadline). It rides the emitted payload as
        `responseMinutes`, and only then, so an assignment made with
        acceptance off sends exactly the body it always did.
      -->
      <LwField
        v-if="requireAcceptance"
        v-model="responseMinutesText"
        class="lw-p-assignee-picker__reply-within"
        type="number"
        inputmode="numeric"
        label="Reply within (minutes)"
        description="The driver accepts or declines in the app. 0 means no deadline."
        :error="responseMinutesError"
        data-test="assignee-reply-within"
      />
      <LwField v-model="query" label="Find a driver or subhauler" type="search" />
      <LwNotice v-if="problem" tone="danger" :title="problem.title">
        <ul v-if="problem.reasons && problem.reasons.length" class="lw-p-assignee-picker__list">
          <li v-for="(line, i) in problem.reasons" :key="i">{{ line }}</li>
        </ul>
      </LwNotice>
      <!--
        data-tour: the assign walkthrough points at this table to say what it
        shows, then rings the free driver's Select button, keyed by name
        (tours/definitions/assign.js; tours/__tests__/targets.test.js).
      -->
      <LwTable
        data-tour="dispatch.assignee-table"
        :columns="columns"
        :rows="visibleRows"
        row-key="key"
        density="compact"
        :loading="loading"
        :error="null"
        caption="Drivers and subhaulers"
        empty-title="Nobody matches"
        empty-body="Clear the search to see every driver and subhauler."
      >
        <template #cell-name="{ row }">
          <span class="lw-p-assignee-picker__name">{{ row.name }}</span>
          <span class="lw-p-assignee-picker__kind">{{
            row.kind === 'subhauler' ? 'Subhauler' : 'Driver'
          }}</span>
        </template>
        <template #cell-availability="{ row }">
          <span class="lw-p-assignee-picker__availability">
            <LwChip
              v-if="row.kind === 'driver' && row.status"
              axis="feedback"
              :value="chipFor(row.status)"
              :label="chipLabel(row)"
            />
            <LwChip
              v-else-if="row.kind === 'subhauler' && row.insuranceExpired"
              axis="feedback"
              value="warning"
              label="Insurance expired"
            />
            <span
              v-for="(line, i) in linesFor(row)"
              :key="i"
              class="lw-p-assignee-picker__reason"
              >{{ line }}</span
            >
            <template v-if="hosFor(row)">
              <span class="lw-p-assignee-picker__hos" data-test="assignee-hos">{{
                hosLine(hosFor(row), hosReadAt)
              }}</span>
              <span
                v-if="hosDetail(hosFor(row))"
                class="lw-p-assignee-picker__reason"
                data-test="assignee-hos-detail"
                >{{ hosDetail(hosFor(row)) }}</span
              >
            </template>
          </span>
        </template>
        <template #cell-action="{ row }">
          <span v-if="row.current" class="lw-p-assignee-picker__current">Assigned</span>
          <LwButton
            v-else-if="actionFor(row) === 'assign'"
            size="sm"
            :variant="row.kind === 'driver' && row.status === 'available' ? 'primary' : 'secondary'"
            :disabled="busy"
            :aria-label="`Select ${row.name}`"
            :data-test="`assignee-select-${row.key}`"
            data-tour="dispatch.assignee-select"
            :data-tour-key="row.name"
            @click="select(row)"
          >
            Select
          </LwButton>
          <LwButton
            v-else-if="actionFor(row) === 'override'"
            size="sm"
            variant="secondary"
            :disabled="busy"
            :aria-label="`Select ${row.name}, with a reason`"
            :data-test="`assignee-select-${row.key}`"
            @click="select(row)"
          >
            Select…
          </LwButton>
          <span v-else class="lw-p-assignee-picker__unavailable">{{ unavailableWord(row) }}</span>
        </template>
      </LwTable>
      <div v-if="hos" class="lw-p-assignee-picker__hos-note" data-test="assignee-hos-footer">
        <p class="lw-p-assignee-picker__hint">{{ hosSourceSentence(hos.providers) }}</p>
        <p
          v-for="(line, i) in hosErrors"
          :key="i"
          class="lw-p-assignee-picker__hint"
          data-test="assignee-hos-error"
        >
          {{ line }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import LwButton from '../../components/lw/LwButton.vue';
import LwChip from '../../components/lw/LwChip.vue';
import LwField from '../../components/lw/LwField.vue';
import LwNotice from '../../components/lw/LwNotice.vue';
import LwTable from '../../components/lw/LwTable.vue';
import AvailabilityNotice from '../../components/scheduling/AvailabilityNotice.vue';
import { orgSetting } from '../../lib/orgSettings.js';
import {
  availabilityChipLabel,
  availabilityLines,
  decisionTone,
  hasHardBlock,
  hosDetail,
  hosLine,
  hosSourceSentence,
} from '../Scheduling/schedulingFormat';

const STATUS_ORDER = { available: 0, needs_review: 1, unknown: 2, blocked: 3 };

// The API's bounds for assignment_response_minutes (0 = no deadline, at most
// a week), and the registry's default when the organization has not loaded.
const RESPONSE_MINUTES_MAX = 10080;
const RESPONSE_MINUTES_FALLBACK = 60;

export default {
  name: 'AssigneePicker',
  components: { AvailabilityNotice, LwButton, LwChip, LwField, LwNotice, LwTable },
  props: {
    // The board row being assigned (id, driver, subhauler).
    load: {
      type: Object,
      required: true,
    },
    // The board's own lists, used for names when the availability read fails
    // and for the subhaulers (who have no schedule to check).
    drivers: {
      type: Array,
      default: () => [],
    },
    subhaulers: {
      type: Array,
      default: () => [],
    },
    zone: {
      type: String,
      required: true,
    },
    // scheduling.manage: may type a reason to assign over a warning.
    canOverride: {
      type: Boolean,
      default: false,
    },
    // The board's PATCH is in flight.
    busy: {
      type: Boolean,
      default: false,
    },
    // The board's refusal (lib's assignmentProblem shape) after a 409.
    problem: {
      type: Object,
      default: null,
    },
  },
  emits: ['assign'],
  data() {
    return {
      decisions: null,
      loading: true,
      failed: false,
      query: '',
      pending: null,
      reason: '',
      // Spec 46: GET /hos/clocks when the tenant has an HOS-capable
      // connection (`enabled`), else null; and when it arrived, for the ages.
      hos: null,
      hosReadAt: Date.now(),
      // Spec 39: the "Reply within" field's text, seeded from the tenant's
      // dispatch.acceptance_deadline_minutes.
      responseMinutesText: String(
        orgSetting(this.$store, 'dispatch.acceptance_deadline_minutes', RESPONSE_MINUTES_FALLBACK)
      ),
    };
  },
  computed: {
    columns() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'availability', label: 'Availability' },
        { key: 'action', label: 'Assign', width: '7rem' },
      ];
    },
    currentKey() {
      if (this.load.subhauler) return `subhauler:${this.load.subhauler.id}`;
      if (this.load.driver) return `driver:${this.load.driver.id}`;
      return null;
    },
    driverRows() {
      const decided = new Map(
        ((this.decisions && this.decisions.drivers) || []).map(d => [Number(d.driver_id), d])
      );
      const source =
        this.decisions && this.decisions.drivers ? this.decisions.drivers : this.drivers;
      return source.map(d => {
        const id = Number(d.driver_id != null ? d.driver_id : d.id);
        const decision = decided.get(id) || {};
        return {
          key: `driver:${id}`,
          kind: 'driver',
          id,
          name: d.name,
          status: decision.status || null,
          reasons: decision.reasons || [],
          travel: decision.travel || null,
          next_windows: decision.next_windows || null,
          override: decision.override || null,
        };
      });
    },
    // Active subhaulers, plus the one this load already has even if it has
    // since been made inactive (the old select's rule).
    subhaulerRows() {
      const current = this.load.subhauler ? this.load.subhauler.id : null;
      return this.subhaulers
        .filter(s => s.active !== false || s.id === current)
        .map(s => ({
          key: `subhauler:${s.id}`,
          kind: 'subhauler',
          id: Number(s.id),
          name: s.name,
          insuranceExpired: !!s.insurance_expired,
        }));
    },
    rows() {
      const drivers = [...this.driverRows].sort(
        (a, b) =>
          (STATUS_ORDER[a.status] ?? 4) - (STATUS_ORDER[b.status] ?? 4) ||
          String(a.name).localeCompare(String(b.name))
      );
      const subhaulers = [...this.subhaulerRows].sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      );
      // Whoever has the load now sits at the top, marked Assigned.
      return [...drivers, ...subhaulers]
        .map(row => ({ ...row, current: row.key === this.currentKey }))
        .sort((a, b) => Number(b.current) - Number(a.current));
    },
    visibleRows() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter(r => r.current || String(r.name).toLowerCase().includes(q));
    },
    // Spec 39: dispatch.require_acceptance, off by default (D8).
    requireAcceptance() {
      return orgSetting(this.$store, 'dispatch.require_acceptance', false) === true;
    },
    // The field as a whole number in the API's range, or null when it is not one.
    responseMinutes() {
      const text = String(this.responseMinutesText ?? '').trim();
      if (!/^\d+$/.test(text)) return null;
      const n = Number(text);
      return n <= RESPONSE_MINUTES_MAX ? n : null;
    },
    responseMinutesError() {
      if (!this.requireAcceptance || this.responseMinutes !== null) return null;
      return `Enter whole minutes from 0 to ${RESPONSE_MINUTES_MAX}.`;
    },
    // A provider that could not be read says so in the footer, quietly: the
    // drivers it would have covered simply show no clock.
    hosErrors() {
      if (!this.hos) return [];
      return (this.hos.providers || []).filter(p => p && p.error).map(p => p.error);
    },
    hint() {
      if (this.failed) {
        return 'Availability could not be checked; the server still checks when you assign.';
      }
      return null;
    },
  },
  watch: {
    // Back from the reason step when the board reports the save went through
    // or the dialog moved on to another load.
    load() {
      this.pending = null;
      this.reason = '';
      this.fetchDecisions();
    },
  },
  created() {
    // Side by side: the table never waits on the clocks.
    this.fetchDecisions();
    this.fetchClocks();
  },
  methods: {
    hosLine,
    hosDetail,
    hosSourceSentence,
    // Spec 46. Asked once per picker (the clocks are per driver, not per
    // load, and the server caches them for two minutes). Anything but an
    // `enabled: true` answer, a refusal included, shows nothing about HOS.
    fetchClocks() {
      return new Promise(resolve => resolve(this.$http.get('/hos/clocks')))
        .then(res => {
          const body = res && res.data;
          const ok =
            body &&
            body.enabled === true &&
            body.drivers &&
            typeof body.drivers === 'object' &&
            !Array.isArray(body.drivers);
          this.hos = ok ? body : null;
          this.hosReadAt = Date.now();
        })
        .catch(() => {
          this.hos = null;
        });
    },
    hosFor(row) {
      if (!this.hos || row.kind !== 'driver') return null;
      return this.hos.drivers[String(row.id)] || null;
    },
    fetchDecisions() {
      this.loading = true;
      this.failed = false;
      return Promise.resolve(
        this.$http.get(`/scheduling/availability?load_id=${encodeURIComponent(this.load.id)}`)
      )
        .then(res => {
          this.decisions = res && res.data && Array.isArray(res.data.drivers) ? res.data : null;
          this.failed = !this.decisions;
        })
        .catch(() => {
          this.decisions = null;
          this.failed = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 'assign' (Select assigns now), 'override' (Select opens the reason
    // step), or null (no button: nothing this viewer can do gets them on).
    actionFor(row) {
      if (row.kind === 'subhauler') return 'assign';
      const override = row.override;
      if (!override || !row.status || row.status === 'available') return 'assign';
      const blocking = override.blocking || [];
      const withReason = override.with_reason || [];
      if (!blocking.length) return 'assign';
      // Spec 38: a reason marked `overridable: false` (a hold)
      // refuses everyone, scheduling managers included, whatever the
      // override summary says; the way past it is on the truck, not here.
      if (withReason.length || hasHardBlock(row.reasons) || !this.canOverride) return null;
      return 'override';
    },
    unavailableWord(row) {
      return (row.override && (row.override.with_reason || []).length) || hasHardBlock(row.reasons)
        ? 'Can’t assign'
        : 'Needs scheduling rights';
    },
    select(row) {
      if (this.actionFor(row) === 'override') {
        this.pending = row;
        this.reason = '';
        return;
      }
      this.emitAssign({ kind: row.kind, id: row.id, reason: null });
    },
    assignPending() {
      const reason = this.reason.trim();
      if (!this.pending || !reason) return;
      this.emitAssign({ kind: 'driver', id: this.pending.id, reason });
    },
    // Only a driver gets an offer; a subhauler has no app to accept in. An
    // unreadable deadline is not sent (the field shows why) and the server
    // applies the tenant default.
    emitAssign(payload) {
      if (this.requireAcceptance && payload.kind === 'driver' && this.responseMinutes !== null) {
        this.$emit('assign', { ...payload, responseMinutes: this.responseMinutes });
        return;
      }
      this.$emit('assign', payload);
    },
    back() {
      this.pending = null;
      this.reason = '';
    },
    chipFor(status) {
      return decisionTone(status);
    },
    chipLabel(row) {
      return availabilityChipLabel(row);
    },
    linesFor(row) {
      if (row.kind === 'subhauler') {
        return row.insuranceExpired
          ? ['You can still assign; check their certificate before it moves.']
          : [];
      }
      return availabilityLines(row, this.zone);
    },
  },
};
</script>

<style scoped lang="scss">
.lw-p-assignee-picker {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
}

.lw-p-assignee-picker__hint {
  margin: 0;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-p-assignee-picker__name {
  display: block;
  font: var(--lw-type-body);
  font-weight: 600;
}

.lw-p-assignee-picker__kind {
  display: block;
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-p-assignee-picker__availability {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--lw-space-1);
}

.lw-p-assignee-picker__reason {
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

// The provider's clock, in the same ink as the text around it: a clock is
// never coloured as good or bad (spec 46 §1.7).
.lw-p-assignee-picker__hos {
  font: var(--lw-type-caption-strong);
  color: var(--lw-fg-primary);
}

.lw-p-assignee-picker__hos-note {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-1);
}

.lw-p-assignee-picker__current,
.lw-p-assignee-picker__unavailable {
  font: var(--lw-type-caption-strong);
  color: var(--lw-fg-secondary);
}

.lw-p-assignee-picker__list {
  margin: 0;
  padding-left: var(--lw-space-4);
}

.lw-p-assignee-picker__step-title {
  margin: 0;
  font: var(--lw-type-body);
}

.lw-p-assignee-picker__step-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--lw-space-3);
}
</style>
